// src/components/DriverList.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  List,
  ListItemText,
  Paper,
  Typography,
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import DriverStatusChip from './DriverStatusChip';
import DriverListActionBar from './DriverListActionBar';
import { getAllDriverStatuses } from '../api';

const DriverList = ({
  drivers,
  selectedDriver,
  onSelectDriver,
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onDriversUpdate,
  onStatusesUpdate,
}) => {
  const [driverStatuses, setDriverStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const token = sessionStorage.getItem('fleetToken');

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  }, [drivers]);

  useEffect(() => {
    const fetchDriverStatuses = async () => {
      if (!drivers || drivers.length === 0 || !token) return;

      setLoading(true);
      try {
        // Single API call to get all driver statuses
        const allStatuses = await getAllDriverStatuses(token);
        
        // Create a lookup map for quick access by driver_uid
        const statusMap = {};
        allStatuses.forEach(status => {
          statusMap[status.driver_uid] = status;
        });
        
        // Map statuses to drivers, handling cases where status might not exist
        const statuses = {};
        drivers.forEach(driver => {
          statuses[driver.uid] = statusMap[driver.uid] || { status: null };
        });
        
        setDriverStatuses(statuses);
        
        // Notify parent component about the statuses for caching
        if (onStatusesUpdate) {
          onStatusesUpdate(statuses);
        }
      } catch (error) {
        console.error('Error fetching driver statuses:', error);
        // Set empty statuses on error
        const statuses = {};
        drivers.forEach(driver => {
          statuses[driver.uid] = { status: null };
        });
        setDriverStatuses(statuses);
        
        // Notify parent component about the error statuses
        if (onStatusesUpdate) {
          onStatusesUpdate(statuses);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDriverStatuses();
  }, [drivers, token]);

  const filteredDrivers = drivers.filter((driver) => {
    const searchMatch =
      driver.uid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.surnames.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (driver.code && driver.code.toLowerCase().includes(searchTerm.toLowerCase()));

    if (statusFilter === 'all') {
      return searchMatch;
    }

    const driverStatus = driverStatuses[driver.uid];

    if (statusFilter === 'unknown') {
      return searchMatch && (!driverStatus || driverStatus.status === null);
    }

    return (
      searchMatch &&
      driverStatus &&
      driverStatus.status === parseInt(statusFilter)
    );
  });

  const statusCounts = Object.values(driverStatuses).reduce(
    (counts, status) => {
      if (status && status.status !== null) {
        counts[status.status] = (counts[status.status] || 0) + 1;
      } else if (status && status.status === null) {
        counts['unknown'] = (counts['unknown'] || 0) + 1;
      }
      return counts;
    },
    {}
  );

  const totalDrivers = drivers.length;

  const handleSelectDriver = (driver) => {
    if (selectedDriver && selectedDriver.uid === driver.uid) {
      onSelectDriver(null); // Unselect if clicking the same driver
    } else {
      onSelectDriver(driver);
    }
  };

  return (
    <Container component="main" maxWidth="md" sx={{ width: '100%' }}>
      <Paper elevation={0} sx={{ mt: 2, p: 2, width: '100%' }}>
        <Typography component="h1" variant="h6" gutterBottom>
          Driver List
        </Typography>

        <DriverListActionBar
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          statusCounts={statusCounts}
          totalDrivers={totalDrivers}
          onStatusClick={onStatusFilterChange}
          selectedStatus={statusFilter}
          onDriversUpdate={onDriversUpdate}
        />

        <Divider sx={{ mb: 1 }} />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, minHeight: '24px' }}>
            <CircularProgress size={20} />
          </Box>
        )}

        <List
          ref={listRef}
          sx={{
            maxHeight: 'calc(100vh - 250px)',
            overflowY: 'auto',
            width: '100%',
          }}
        >
          {filteredDrivers.map((driver) => {
            const status = driverStatuses[driver.uid]
              ? driverStatuses[driver.uid].status
              : null;
            const isSelected =
              selectedDriver && selectedDriver.uid === driver.uid;

            return (
              <ListItemButton
                key={driver.uid}
                onClick={() => handleSelectDriver(driver)}
                sx={{
                  py: 0.5,
                  '&:hover': { backgroundColor: 'action.hover' },
                  backgroundColor: isSelected
                    ? 'action.selected'
                    : 'inherit',
                  width: '100%',
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap>
                      {`${driver.name} ${driver.surnames}`}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {`${driver.uid} • ${driver.code}`}
                    </Typography>
                  }
                  sx={{ minWidth: 0, flex: 1 }}
                />
                {status !== null && (
                  <Box sx={{ minWidth: '65px', display: 'flex', justifyContent: 'flex-end' }}>
                    <DriverStatusChip status={status} />
                  </Box>
                )}
                {isSelected && (
                  <ListItemIcon sx={{ ml: 1, minWidth: 'auto' }}>
                    <Check fontSize="small" />
                  </ListItemIcon>
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Paper>
    </Container>
  );
};

export default DriverList;
